<script>
import ClickOutside from '@/system/directives/clickOutside';

export default {
    name: 'ColorPicker',

    directives: { ClickOutside },

    data() {
        return {
            color: this.stored_color,
            isColorPickerOpen: false,
        }
    },

    watch: {
        stored_color: function() {
            this.color = this.stored_color || '';
        }
    },

    methods: {
        changeColor(color) {
            this.color = color;
            this.isColorPickerOpen = false;
            this.$emit('changeColor', color);
        },

        togglePicker() {
            if (!this.disabled) {
                this.isColorPickerOpen = !this.isColorPickerOpen;
            }
        },

        onClickOutside() {
            this.isColorPickerOpen = false;
        },

        onCustomColorChange(e) {
            this.color = e.target.value;
            this.$emit('changeColor', this.color);
        },

        handleOk() {
            this.changeColor(this.color);
        }
    },

    props: {
        stored_color: {
            default: ''
        },
        colors: {
            default: () => ([
                '',
                '#f7dc6f',
                '#143642',
                '#ec9a29',
                '#a8201a',
                '#dad2d8',
                '#0f8b8d',
                '#f5b041',
                '#58d68d',
                '#45b39d',
                '#5dade2',
                '#a569bd',
                '#ec7063',
                '#ffeb3b',
                '#8bc34a',
                '#009688',
                '#03a9f4',
                '#3f51b5',
                '#9c27b0',
                '#b03a2e',
            ])
        },
        border: {
            default: true
        },
        disabled: {
            default: false
        },
        wrapper_style: {
            type: Object,
            default: () => ({
                width: '183px'
            })
        },
        trigger_style: {
            type: Object,
            default: () => ({
                width: '100%',
                height: '38px'
            })
        },
        disableTitle: {
            type: Boolean,
            default: false
        },
        position: {
            type: String,
            default: 'left'
        }
    }
}
</script>

<template>
    <div class="color-picker" v-click-outside="onClickOutside">
        <label v-show="!disableTitle" class="control-label">
            {{ trans('global.colour_select') }}
        </label>
        
        <div 
            class="color-picker--color-trigger"
            :class="{ 'disabled': disabled, 'with-border': border }"
            :style="trigger_style"
            @click="togglePicker"
        >
        <div 
            class="color-picker--color-preview"
            :style="{ backgroundColor: color || '#fff' }"
        ></div>
        </div>

        <Transition>
            <div 
                v-if="isColorPickerOpen"
                class="color-picker--color-popup"
                :class="position"
                :style="wrapper_style"
            >
                <div class="color-picker--color-grid">
                    <button
                        v-for="(swatch, index) in colors"
                        type="button"
                        :key="index"
                        class="color-picker--color-grid--color-swatch"
                        :class="{ 'selected': swatch === color, 'blank': !swatch }"
                        :style="{ backgroundColor: swatch || '#fff' }"
                        @click="changeColor(swatch)"
                    ></button>
                </div>
                
                <div class="color-picker--custom-color-picker">
                    <div class="color-picker--custom-color-picker--picker-container">
                        <input 
                            type="color"
                            v-model="color"
                            @change="onCustomColorChange"
                        >
                        <button 
                            class="color-picker--custom-color-picker--ok-button"
                            type="button"
                            @click="handleOk"
                        >
                            Ok
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<style scoped lang="scss">
@import 'Sass/system/global/colors';

.color-picker {
  position: relative;

    &--color-trigger {
        cursor: pointer;
        border-radius: 4px;
        overflow: hidden;
    }

    &--color-trigger.disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &--color-trigger.with-border {
        border: 1px solid #ddd;
    }

    &--color-preview {
        width: 100%;
        height: 100%;
    }

    &--color-popup {
        position: absolute;
        top: 100%;
        background: white;
        border: 1px solid #ddd;
        border-radius: 5px;
        padding: 8px;
        z-index: 1000;
        box-shadow: 0 6px 14px 0 rgba(0, 0, 0, 0.2), 0 4px 16px 0 rgba(0, 0, 0, 0.19);

        &.right {
            left: 0;
        }

        &.left {
            right: 0;
        }
    }

    &--color-grid {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 7px;
        margin-bottom: 8px;

        &--color-swatch {
            width: 28px;
            height: 28px;
            border: none;
            border-radius: 7px;
            box-shadow: inset 0 0 2px rgba(0, 0, 0, .65);
            cursor: pointer;
            padding: 0;
        }

        &--color-swatch:hover {
            opacity: 0.85;
        }

        &--color-swatch.selected {
            position: relative;
        }

        &--color-swatch:not(.blank).selected::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 16px;
            height: 16px;
            background: rgba(0, 0, 0, 0.3);
            border-radius: 50%;
            z-index: 1;
        }

        &--color-swatch:not(.blank).selected::after {
            content: '';
            position: absolute;
            top: 45%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(45deg);
            width: 5px;
            height: 10px;
            border: solid white;
            border-width: 0 2px 2px 0;
            z-index: 2;
        }

        &--color-swatch.blank {
            background: #fff;
            position: relative;
        }

        &--color-swatch.blank::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            width: 100%;
            height: 1px;
            background: #ff0000;
            transform: rotate(45deg);
        }
    }

    &--custom-color-picker {
        border-top: 1px solid #ddd;
        padding-top: 8px;

        &--picker-container {
            display: flex;
            gap: 8px;
            align-items: center;
        }

        &--picker-container input[type="color"] {
            cursor: pointer;
            flex: 1;
            height: 30px;
            padding: 0;
            border: none;
        }

        &--ok-button {
            display: inline-block;
            padding: 5px 12px;
            margin-bottom: 0;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.42857143;
            text-align: center;
            white-space: nowrap;
            vertical-align: middle;
            -ms-touch-action: manipulation;
            touch-action: manipulation;
            cursor: pointer;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            background-image: none;
            border: 1px solid $primary-color;
            border-radius: 4px;
            background-color: $primary-color;
            color: #fff;
        }
    }
}

.v-enter-active,
.v-leave-active {
    transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>